import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Explicatia care apare la hover sau la focus pe o eticheta (Madalin, 12.10.2026: „hover cu
 * explicatii pe tot ce are nevoie de explicatii in plus").
 *
 * Inlocuieste `title=`: acela apare dupa o secunda, nu se poate citi pe telefon si nu se vede la
 * navigarea din tastatura. Eticheta primeste o subliniere punctata, ca sa se stie ca are ceva
 * de spus.
 *
 * Bula se deseneaza in `document.body`, cu pozitie fixa calculata din eticheta: in tabelele cu
 * `overflow-auto` o bula pusa langa eticheta ar fi taiata de marginea containerului.
 */
export function Explicatie({
  text,
  children,
  subliniat = true,
  focalizabil = true,
}: {
  text: ReactNode
  children: ReactNode
  /** `false` pentru iconite sau butoane, care se vad deja ca interactive. */
  subliniat?: boolean
  /**
   * `false` cand eticheta sta deja intr-un buton (capul de coloana sortabil): un element
   * focalizabil in alt element focalizabil incurca tastatura si cititoarele de ecran.
   */
  focalizabil?: boolean
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [pozitie, setPozitie] = useState<{ x: number; y: number; jos: boolean } | null>(null)

  const arata = () => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    // deasupra, daca incape; altfel dedesubt
    const jos = r.top < 90
    setPozitie({ x: r.left + r.width / 2, y: jos ? r.bottom + 6 : r.top - 6, jos })
  }
  const ascunde = () => setPozitie(null)

  // la derulare bula ar ramane in urma etichetei
  useLayoutEffect(() => {
    if (!pozitie) return
    window.addEventListener('scroll', ascunde, true)
    return () => window.removeEventListener('scroll', ascunde, true)
  }, [pozitie])

  return (
    <>
      <span
        ref={ref}
        tabIndex={focalizabil ? 0 : undefined}
        onMouseEnter={arata}
        onMouseLeave={ascunde}
        onFocus={arata}
        onBlur={ascunde}
        className={`rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-b4l-blue ${
          subliniat
            ? 'cursor-help underline decoration-b4l-text-dim decoration-dotted underline-offset-[3px]'
            : ''
        }`}
      >
        {children}
      </span>
      {pozitie &&
        createPortal(
          <div
            role="tooltip"
            style={{
              left: Math.min(Math.max(pozitie.x, 140), window.innerWidth - 140),
              top: pozitie.y,
            }}
            className={`pointer-events-none fixed z-[60] w-max max-w-[260px] -translate-x-1/2 rounded-caseta border border-b4l-border bg-b4l-surface-ridicata px-2.5 py-1.5 text-mic font-normal normal-case tracking-normal text-b4l-text shadow-ridicat ${
              pozitie.jos ? '' : '-translate-y-full'
            }`}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  )
}
